import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, PlayCircle, Video } from 'lucide-react';
import { AppButton, ProgressBar, SegmentedControl } from '../../../ui';
import DashCard, { CardEmpty, CardLink } from '../shared/DashCard';
import IconChip from '../shared/IconChip';
import { canJoin, clampPct, courseKinds, unitsLabel } from '../studentHomeRules';

const VISIBLE_COURSES = 3;

const continueTarget = (course) => (
  course.kind === 'self-paced'
    ? { to: `/s/self-paced/${course.id}/learn` }
    : { to: '/s/learning', state: { preselectBatchId: course.batchId, preselectCourseId: course.id } }
);

const CourseAction = ({ course }) => {
  if (canJoin(course)) {
    return (
      <AppButton as="a" href={`/s/classroom/${course.batchId}`} variant="success" size="sm" className="tw-shrink-0 tw-no-underline">
        <Video size={16} />
        Join
      </AppButton>
    );
  }
  return (
    <AppButton as={Link} {...continueTarget(course)} size="sm" className="tw-shrink-0 tw-no-underline">
      Continue
    </AppButton>
  );
};

const CourseRow = ({ course }) => {
  const percent = clampPct(course.progress);
  const details = [course.code, unitsLabel(course)].filter(Boolean).join(' · ');
  return (
    <li className="tw-flex tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-line-soft tw-py-3 first:tw-pt-0 last:tw-border-b-0 last:tw-pb-0">
      <IconChip icon={course.kind === 'self-paced' ? PlayCircle : BookOpen} size={38} iconSize={18} />
      <div className="tw-min-w-0 tw-flex-1">
        <div className="tw-line-clamp-2 tw-text-sm tw-font-semibold tw-leading-snug tw-text-content">{course.name}</div>
        {details && <div className="tw-mt-0.5 tw-truncate tw-text-[0.6875rem] tw-text-content-subtle">{details}</div>}
        <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2.5">
          <ProgressBar value={percent} size="md" color="primary" className="tw-min-w-0 tw-flex-1" />
          <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-content-muted">{percent}%</span>
          <CourseAction course={course} />
        </div>
      </div>
    </li>
  );
};

/** A live class always leads its list, so it can never be hidden behind "View all". */
const MyCoursesCard = ({ courses, className }) => {
  const kinds = useMemo(() => courseKinds(courses), [courses]);
  const [picked, setPicked] = useState(null);
  const active = kinds.find((kind) => kind.value === picked) || kinds[0];

  return (
    <DashCard
      title="My Courses"
      className={className}
      action={<CardLink to={active?.value === 'self-paced' ? '/s/self-paced/enrollments' : '/s/learning'}>View all</CardLink>}
    >
      {kinds.length > 1 && (
        <SegmentedControl
          label="Show courses"
          size="xs"
          value={active.value}
          onChange={setPicked}
          options={kinds.map(({ value, label, count }) => ({ value, label, count }))}
          className="tw-mb-3 tw-self-start"
        />
      )}
      {active ? (
        <ul className="tw-m-0 tw-list-none tw-p-0">
          {active.courses.slice(0, VISIBLE_COURSES).map((course) => <CourseRow key={course.id} course={course} />)}
        </ul>
      ) : (
        <CardEmpty>
          <span>
            You have no active courses yet.{' '}
            <Link to="/s/courses" className="tw-font-semibold tw-text-primary tw-no-underline hover:tw-underline">Browse courses</Link>
          </span>
        </CardEmpty>
      )}
    </DashCard>
  );
};

export default MyCoursesCard;
